<template>
  <div class="hwh-calculator__block">
    <div class="hwh-calculator__sub">Please confirm the information below is correct.</div>
    <div class="hwh-calculator__zip-block">
      <div class="hwh-calculator__zip-block-inner">
        Zip Code:
        <input type="text" :maxlength="zipCodeLength" class="hwh-calculator__input" v-model="zipCode" ref="zipCode"
          @keydown="validateZipCode" @focusout="getStateFromZip" />
        <div class="hwh-calculator__zip-block-error" v-if="showError && zipCode">Incorrect zip code</div>
        <div class="hwh-calculator__zip-block-error" v-if="isLoading">
          <div class="hwh-calculator__spinner"></div> Data loading...
        </div>
      </div>
    </div>
    <div class="hwh-calculator__row hwh-calculator__row--350 hwh-calculator__cost-rate-wrapper-mob">
      <div
        class="hwh-calculator__col hwh-calculator__col--250 hwh-calculator__col--center hwh-calculator__col--border hwh-calculator__cost--border ">
        <div class="hwh-calculator__title">Estimated Project Cost:</div>
        <div class="hwh-calculator__text">We have prefilled the hybrid electric heat pump water heater retail price and
          average installation costs based on a 50-gallon model. Your local costs may vary.</div>
        <div class="hwh-calculator__input-block">
          $
          <input type="text" class="hwh-calculator__input" v-model="hybridPrice" @focusout="changeHybridPrice"
            @keydown="preventNonFloatKeys($event)" disabled="disabled" />
        </div>
        <div class="hwh-calculator__hint hwh-calculator__hint--mb20">Hybrid Electric HPWH estimated price</div>
        <div class="hwh-calculator__input-block">
          $
          <input type="text" class="hwh-calculator__input" v-model="hybridInstallationPrice"
            @focusout="changeHybridInstallationPrice" @keydown="preventNonFloatKeys($event)" disabled="disabled" />
        </div>
        <div class="hwh-calculator__hint">Estimated installation cost</div>
      </div>
      <div class="hwh-calculator__col hwh-calculator__col--250 hwh-calculator__col--center">
        <div class="hwh-calculator__title">Average Energy Rate:</div>
        <div class="hwh-calculator__text">Below please enter the average energy rate for your zip code. Typically, this
          rate can be found on your utility bill.</div>
        <div class="hwh-calculator__input-block">
          $
          <input type="text" class="hwh-calculator__input" v-model="avgEnergyRate" ref="avgEnergyRate"
            @focusout="changeAvgEnergyRate" @keydown="preventNonFloatKeys($event)" />
          ¢/KWH
        </div>
        <div class="hwh-calculator__hint">The national average rate ${{ avgRate }}/KWH</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import config from '@/config';
import { getInfoByZip } from '../../api/ApiService';

export default {
  name: 'Step1',
  props: {},
  data() {
    return {
      zipCode: null,
      hybridPrice: null,
      hybridInstallationPrice: null,
      avgEnergyRate: null,
      maxHybridPrice: config.MAX_HYBRID_PRICE,
      avgRate: config.AVERAGE_RATE,
      zipCodeLength: config.ZIP_CODE_LENGTH,
      incentiveState: null,
      stateAbbreviation: null,
      rates: config.RATES,
      showError: false,
    };
  },
  computed: {
    ...mapState(['validHybridPrice', 'validHybridInstallationPrice', 'validAvgEnergyRate', 'validZipCode', 'isLoading', 'isUS']),
  },
  mounted() {
    this.zipCode = this.validZipCode;
    if (!this.zipCode) {
      this.zipCode = this.getZipFromCookie();
      if (this.zipCode) {
        this.getStateFromZip();
      }
    }
    this.hybridPrice = this.validHybridPrice;
    this.hybridInstallationPrice = this.validHybridInstallationPrice;
    this.avgEnergyRate = this.validAvgEnergyRate;
  },
  methods: {
    changeAvgEnergyRate(e) {
      let { value } = e.target;
      value = parseFloat(value) || null;
      this.avgEnergyRate = value;
      this.setAvgEnergyRate(value);
    },
    changeHybridPrice(e) {
      let { value } = e.target;
      value = parseFloat(value) || null;
      if (value > this.validHybridPrice || !value) {
        value = this.maxHybridPrice;
      }
      this.setValidHybridPrice(value);
      this.hybridPrice = value;
    },
    changeHybridInstallationPrice(e) {
      let { value } = e.target;
      value = parseFloat(value) || null;
      this.setValidHybridInstallationPrice(value);
      this.hybridInstallationPrice = value;
    },
    async getStateFromZip() {
      this.setIsLoading(true);
      const data = await getInfoByZip(this.zipCode);
      const state = data?.places[0]?.state;
      const stateAbbreviation = data?.places[0] ? data?.places[0]['state abbreviation'] : null;
      if (state) {
        this.incentiveState = state;
        this.stateAbbreviation = stateAbbreviation;
        this.setValidZipCode(this.zipCode);
        this.setStateAbbreviation(stateAbbreviation);
        this.resetElectricRateByState();
        await this.getRebatesForZip();
      } else {
        this.showError = true;
        this.clearZip();
      }
      this.setIsLoading(false);
    },
    clearZip() {
      this.setValidZipCode(null);
      this.setAvgEnergyRate(null);
      this.avgEnergyRate = null;
    },
    async getRebatesForZip() {
      await this.getRebatePrograms();
    },
    resetElectricRateByState() {
      this.rates.forEach((item) => {
        if (item.state.toLowerCase() === this.incentiveState.toLowerCase()) {
          this.setAvgEnergyRate(item.rate);
          this.avgEnergyRate = item.rate;
        }
      });
    },
    preventNonFloatKeys(e) {
      const keycode = e.charCode || e.keyCode;
      if ((!e.shiftKey && (keycode >= 48 && keycode <= 57)) // NoShift and Numrow Numbers
        || (keycode >= 96 && keycode <= 105) // Numpad Numbers
        || keycode === 190 || keycode === 110 // Period and Decimal Point
        || keycode === 8 || keycode === 46) { // Backspace and Delete
        return true;
      }
      e.preventDefault();
      return false;
    },
    validateZipCode(e) {
      this.showError = false;
      if (this.isUS) {
        const keycode = e.charCode || e.keyCode;
        if ((keycode === 8 || keycode === 46) // Backspace and Delete
          || (keycode === 37 || keycode === 39)) { // Left and Right
          return true;
        }
        if ((!e.shiftKey && (keycode >= 48 && keycode <= 57)) // NoShift and Numrow Numbers
          || (keycode >= 96 && keycode <= 105)) { // Numpad Numbers
          return true;
        }
        e.preventDefault();
        return false;
      }
      return true;
    },
    getZipFromCookie() {
      const locationInfo = decodeURIComponent(document.cookie)
        .split(';')
        .find((item) => item.indexOf('PostalCodeLookup=') > -1);
      if (!locationInfo) return '';
      const zipCode = locationInfo
        .trim()
        .split('PostalCode=')
        .at(-1);
      if (!zipCode
        || zipCode.length !== 5
        || (Number.isNaN(zipCode) || Number.isNaN(parseFloat(zipCode)))) return '';
      return zipCode;
    },
    ...mapActions([
      'getRebatePrograms',
      'setValidZipCode',
      'setStateAbbreviation',
      'setValidHybridPrice',
      'setValidHybridInstallationPrice',
      'setAvgEnergyRate',
      'setIsLoading',
    ]),
  },
};
</script>
